/**
 * Experiment: talk to the Business NXT editor over postMessage instead of encoding the
 * session into the iframe URL. Kept free of any dependency on DocumentEditor.component.
 */

export const BNXT_APPLICATION_NAME = "Visma Business Cloud";

// A JWS is three base64url segments.
const JWS_PATTERN = /^[\w-]+\.[\w-]+\.[\w-]+$/;

export interface BnxtEditRequest {
    companyId: string;
    systemId: string;
    userId: string;
    readonly?: boolean;
    administrator?: boolean;
    taskId?: number | string | null;
}

export const OPEN_TASK_SUCCESS = "openTaskSuccess";
export const OPEN_TASK_ERROR = "openTaskError";

/** what Business NXT answers an openTask with, once it has tried to open the task */
export interface BnxtOpenTaskResult {
    command: typeof OPEN_TASK_SUCCESS | typeof OPEN_TASK_ERROR;
    requestId: string;
}

export interface BnxtOpenTaskMessage {
    command: "openTask";
    approvalToken: string;
    editRequest: BnxtEditRequest;
    requestId: string;
}

export interface BnxtEditorTarget {
    /** what the iframe loads: the editor url the backend sent, minus the signed token */
    url: string;
    /** MessageEvent.origin is only scheme://host, so message checks have to compare against this and not the url */
    origin: string;
    approvalToken: string;
}

/**
 * The backend hands us the editor url with the signed edit request already substituted into it
 * (`https://<editor host>/approval/<jws>`). That trailing path segment is the approval token, and
 * the rest is the editor itself - which we now load bare and hand the token to over postMessage.
 */
export function parseEditorUrl(editorUrl: string | null | undefined): BnxtEditorTarget | null {
    const path = editorUrl?.split(/[?#]/)[0] ?? "";
    const lastSeparator = path.lastIndexOf("/");
    const approvalToken = path.substring(lastSeparator + 1);

    if (!JWS_PATTERN.test(approvalToken)) {
        return null;
    }

    const url = path.substring(0, lastSeparator);

    try {
        return {url, origin: new URL(url).origin, approvalToken};
    } catch {
        return null;
    }
}

// Experiment diagnostics; drop once the handshake is confirmed.
export const BNXT_LOG_PREFIX = "[bnxt-open-task]";

// a message on the editor origin can be anything, so its fields are only read where they exist
type EditorMessage = Partial<Record<"event" | "command" | "type" | "status", unknown>>;

const asEditorMessage = (data: unknown): EditorMessage | undefined =>
    data !== null && typeof data === "object" ? data as EditorMessage : undefined;

// The exact key Business NXT announces itself with isn't pinned down yet, so accept the plausible ones.
export function isEditorReadyMessage(data: unknown): boolean {
    const message = asEditorMessage(data);

    return data === "ready"
        || message?.event === "ready"
        || message?.command === "ready"
        || message?.type === "ready"
        || message?.status === "ready";
}

export function readOpenTaskResult(data: unknown): BnxtOpenTaskResult | null {
    const command = asEditorMessage(data)?.command;

    return command === OPEN_TASK_SUCCESS || command === OPEN_TASK_ERROR ? data as BnxtOpenTaskResult : null;
}

export function buildOpenTaskMessage(approvalToken: string, editRequest: BnxtEditRequest): BnxtOpenTaskMessage {
    return {
        command: "openTask",
        approvalToken,
        editRequest,
        requestId: crypto.randomUUID()
    };
}
